import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { cloneDeep } from "lodash"
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/outline"

import RangeBillingPriceConfigTable from "../RangeBillingPriceConfigTable"
import { NATIONAL_BR_SHIPPING_LABELS_SERVICE_TYPE_CATEGORIES, SERVICE_TYPES } from "../constants"
import { findOrCreateBillingService, getCurrency } from "../utils"
import { UNITS_IDS } from "../RangeBillingPriceConfigTable/constants"

const PRICE_MODES_SHIPPING_LABEL = [UNITS_IDS.TOTAL, UNITS_IDS.EXCESS_PER_UNIT]

const NaturaAdditionalLabelServices = ({ storeBillingServices, onUpdateStoreBillingServices, onIsValid, storeId, warehouse }) => {
    const { i18n } = useTranslation()

    const tableHeaders = [
        { label: i18n.t("billing.table_headers.from_weight"), id: "from" },
        { label: i18n.t("billing.table_headers.to_weight"), id: "to" },
        { label: i18n.t("billing.table_headers.price"), id: "price" },
        { label: i18n.t("billing.table_headers.unit"), id: "unit" },
        { label: null, id: 'actions' }
    ]

    const serviceTypeCategories = Object.values(NATIONAL_BR_SHIPPING_LABELS_SERVICE_TYPE_CATEGORIES)

    const buildInitialValidationState = () => {
        const state = {}
        serviceTypeCategories.forEach(cat => {
            state[cat] = false
        })
        return state
    }
    const [validationStates, setValidationStates] = useState(buildInitialValidationState)
    const lastEmittedValid = useRef(null)

    const additionalLabelServices = serviceTypeCategories.map(serviceTypeCategory => {
        return findOrCreateBillingService({
            serviceType: SERVICE_TYPES.SERVICE_SHIPPING_LABEL_NATURA_VENDA_DIRETA_ADDITIONAL_LABEL,
            serviceTypeCategory: serviceTypeCategory,
            billingServices: storeBillingServices,
            currency: getCurrency(warehouse?.country),
            storeId: storeId,
            warehouseId: warehouse?.id
        })
    })

    const tablesData = additionalLabelServices?.map(billingService => {
        const data = billingService?.price_configuration.map(priceRange => {
            return {
                from: { value: priceRange.from, isDisabled: true, scale: 2 },
                to: { value: priceRange.to, scale: 2 },
                price: { value: priceRange.price },
                price_mode: { value: priceRange.price_mode || UNITS_IDS.TOTAL },
                serviceTypeCategory: { value: priceRange.service_type_category },
                excess: { value: priceRange.excess },
                range_exclusion: { value: priceRange.range_exclusion },
            }
        })
        return { category: billingService.service_type_category, data: data }
    })

    const unitOptionsLabels = {
        TOTAL: i18n.t("billing.natura_services.shipping_unit_options.TOTAL"),
        EXCESS_PER_UNIT: i18n.t("billing.natura_services.shipping_unit_options.EXCESS_PER_UNIT")
    }

    const handleOnUpdateData = (newTableData, service) => {
        let newStoreBillingServices = cloneDeep(storeBillingServices)

        const newPriceConfiguration = newTableData.map(priceRangeData => {
            return {
                from: priceRangeData.from.value,
                to: priceRangeData.to.value,
                price: priceRangeData.price.value,
                price_mode: priceRangeData.price_mode.value,
                excess: priceRangeData.excess.value,
                range_exclusion: priceRangeData.range_exclusion.value
            }
        })

        const serviceExists = newStoreBillingServices.some(({ service_type, service_type_category }) => (
            service_type === service.service_type && service_type_category === service.service_type_category
        ))

        if (serviceExists) {
            newStoreBillingServices = newStoreBillingServices.map(billingService => {
                if (billingService.service_type === service.service_type && billingService.service_type_category === service.service_type_category) {
                    return { ...billingService, price_configuration: newPriceConfiguration }
                }
                return billingService
            })
        } else {
            const newService = cloneDeep(service)
            newService.price_configuration = newPriceConfiguration
            newStoreBillingServices.push(newService)
        }

        onUpdateStoreBillingServices(newStoreBillingServices)
    }

    const handleOnIsValid = (serviceTypeCategory, isValid) => {
        setValidationStates(prev => ({ ...prev, [serviceTypeCategory]: isValid }))
    }

    useEffect(() => {
        const allValid = Object.values(validationStates).every(v => v === true)
        if (lastEmittedValid.current !== allValid) {
            lastEmittedValid.current = allValid
            onIsValid(allValid)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- onIsValid is a stable parent callback, lastEmittedValid ref guards against stale calls
    }, [validationStates])

    return (
        <div>
            {serviceTypeCategories.map(serviceTypeCategory => {
                const categoryServiceData = tablesData.find(({ category }) => category === serviceTypeCategory)
                const categoryService = additionalLabelServices.find(({ service_type_category }) => service_type_category === serviceTypeCategory)

                return (
                    <div key={`natura-additional-${serviceTypeCategory}`} className="flex flex-col gap-2 mb-5">
                        <div>
                            <div className="flex gap-1">
                                <h2 className="text-xl text-gray-500">
                                    <span className="text-red-500">*</span>
                                    {i18n.t(`billing.national_shipping_label_services.titles.${serviceTypeCategory}`)}
                                </h2>
                                {validationStates[serviceTypeCategory] ? (
                                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                ) : (
                                    <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />
                                )}
                            </div>

                            <RangeBillingPriceConfigTable
                                headers={tableHeaders}
                                priceConfiguration={categoryServiceData.data}
                                currency={getCurrency(warehouse?.country)}
                                onUpdatePriceConfiguration={newTableData => handleOnUpdateData(newTableData, categoryService)}
                                onIsValid={(isValid) => handleOnIsValid(serviceTypeCategory, isValid)}
                                availableUnitOptions={PRICE_MODES_SHIPPING_LABEL}
                                unitOptionsLabels={unitOptionsLabels}
                            />
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default NaturaAdditionalLabelServices
